import { Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import * as _ from 'lodash';
import { INgxCrudRouteConfig } from '../_models/_crud-config';
import {
  INgxCrudLayout, INgxCrudLayoutApiConfig,
  INgxCrudLayoutFormConfig, INgxCrudLayoutSettingConfig, INgxCrudLayoutSettingManageActionConfig
} from '../_models/_crud-layout';

@Injectable({
  providedIn: 'root'
})
export class NgxCrudService {

  constructor() { }

  prepareLayout(rawLayout: INgxCrudLayout): INgxCrudLayout {
    if (rawLayout) {
      const layout: INgxCrudLayout = {
        api: this.prepareApiConfig(rawLayout),
        columns: _.has(rawLayout, 'columns') ? rawLayout.columns : [],
        form: this.prepareFormConfig(rawLayout),
        setting: this.prepareSettingConfig(rawLayout)
      };
      return layout;
    }
    return null;
  }

  prepareFormRoute(activatedRoute: ActivatedRoute, callback: (config: INgxCrudRouteConfig, layout: INgxCrudLayout) => void) {
    // set config
    const config = activatedRoute.snapshot.data['config'] as INgxCrudRouteConfig;
    if (config) {
      // set layout key
      config['layoutKey'] = _.has(config, 'layoutKey') ? config.layoutKey : 'layout';
      // get layout object
      const rawLayout = activatedRoute.snapshot.data[config.layoutKey];
      if (rawLayout) {
        // prepare layout
        const layout = this.prepareLayout(rawLayout);
        // call back
        callback(config, layout);
      }
    }
  }

  // prepare api
  private prepareApiConfig(data: INgxCrudLayout): INgxCrudLayoutApiConfig {
    if (_.has(data, 'api')) {
      const api = data.api;
      const apiConfig: INgxCrudLayoutApiConfig = {
        changeStatus: _.has(api, 'changeStatus') ? api.changeStatus : null,
        delete: _.has(api, 'delete') ? api.delete : null,
        manage: _.has(api, 'manage') ? api.manage : null,
        search: _.has(api, 'search') ? api.search : null,
        command: (path) => { return path; }
      };
      return apiConfig;
    }
    return null;
  }

  // prepare setting
  private prepareSettingConfig(data: INgxCrudLayout): INgxCrudLayoutSettingConfig {
    // setting layout
    const settingLayout: INgxCrudLayoutSettingConfig = {} as INgxCrudLayoutSettingConfig;
    // check setting attribute is exist or not
    if (_.has(data, 'setting')) {
      // setting instance
      const setting = data.setting;
      // title
      settingLayout.pageTitle = _.has(setting, 'pageTitle') ? setting.pageTitle : 'NA';
      // prepare manage Config
      this.prepareManageConfigSetting(setting, settingLayout);
      // prepare grid config
      this.prepareGridConfigSetting(setting, settingLayout);
    }
    return settingLayout;
  }

  private prepareManageConfigSetting(setting: INgxCrudLayoutSettingConfig, settingLayout: INgxCrudLayoutSettingConfig) {
    if (_.has(setting, 'actionConfig')) {
      // action config
      const actionConfig = setting.actionConfig;
      // init config
      settingLayout.actionConfig = { actions: [], enable: false };
      // check manage is enabled or not
      settingLayout.actionConfig.enable = _.has(actionConfig, 'enable') ? actionConfig.enable : false;
      if (settingLayout.actionConfig.enable) {
        // get actions
        const actions = _.has(actionConfig, 'actions') ? actionConfig.actions : [];
        // action Null check
        if (actions && actions.length > 0) {
          // loop action info
          actions.forEach(info => {
            // action instance
            const actionInfo: INgxCrudLayoutSettingManageActionConfig = {} as INgxCrudLayoutSettingManageActionConfig;
            // set key
            actionInfo.key = _.has(info, 'key') ? info.key : null;
            // set label
            actionInfo.label = _.has(info, 'label') ? info.label : null;
            // set icon
            actionInfo.icon = _.has(info, 'icon') ? info.icon : null;
            // set styleClass
            actionInfo.styleClass = _.has(info, 'styleClass') ? info.styleClass : 'p-button-sm p-button-outlined';
            // set mode
            actionInfo.mode = _.has(info, 'mode') ? info.mode : null;
            // check for modal config
            if (_.has(info, 'modalConfig')) {
              actionInfo.modalConfig = {
                title: _.has(info, 'modalConfig.title') ? info.modalConfig.title : null,
                width: _.has(info, 'modalConfig.width') ? info.modalConfig.width : 'auto'
              };
            }
            // push
            settingLayout.actionConfig.actions.push(actionInfo);
          });
        }

      }
      // for action
      settingLayout.actionConfig.onAction = (action) => {
        // sudo action
      };
    }
  }

  private prepareGridConfigSetting(setting: INgxCrudLayoutSettingConfig, settingLayout: INgxCrudLayoutSettingConfig) {
    if (_.has(setting, 'gridConfig')) {
      // grid config instance
      const gridConfig = setting.gridConfig;
      // init config
      settingLayout.gridConfig = {};
      // set pk
      settingLayout.gridConfig.pk = _.has(gridConfig, 'pk') ? gridConfig.pk : null;
      // set layoutId
      settingLayout.gridConfig.layoutId = _.has(gridConfig, 'layoutId') ? gridConfig.layoutId : null;
      // set enableSearch
      settingLayout.gridConfig.enableSearch = _.has(gridConfig, 'enableSearch') ? gridConfig.enableSearch : false;
      // set sortField
      settingLayout.gridConfig.sortField = _.has(gridConfig, 'sortField') ? gridConfig.sortField : null;
      // set sortOrder
      settingLayout.gridConfig.sortOrder = _.has(gridConfig, 'sortOrder') ? gridConfig.sortOrder : -1;
      // set sortOrder
      settingLayout.gridConfig.perPage = _.has(gridConfig, 'perPage') ? gridConfig.perPage : 10;
      // set breadcrumbs
      settingLayout.gridConfig.breadcrumbs = _.has(gridConfig, 'breadcrumbs') ? gridConfig.breadcrumbs : [];
      // action
      // sudo action
      settingLayout.gridConfig.action = {
        customAction: {},
        onFilterChanged: (filter) => { },
        loadResult: () => { }
      }
    }
  }

  private prepareFormConfig(data: INgxCrudLayout): INgxCrudLayoutFormConfig {
    // init form form config
    const formConfig: INgxCrudLayoutFormConfig = {} as INgxCrudLayoutFormConfig;
    // check form attribute is exist or not
    if (_.has(data, 'form')) {
      // form instance
      const form = data.form;
      // check control
      if (_.has(form, 'controls')) {
        // set controls
        formConfig.controls = form.controls;
        // prepare action
        // sudo function
        formConfig.action = {
          onExecute: (formValue, action, data, callback) => { },
          onPrepareValue: (formgroup, formItems, data) => { },
          onPatchValue: (formgroup, formItems, data) => { }
        }
      }
    }

    return formConfig;
  }
}
